/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useEffect } from 'react'
import * as z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { SettingsSection } from '../components/settings-section'
import { useUpdateOption } from '../hooks/use-update-option'

const agentLimitsSchema = z.object({
  AgentMaxConcurrentRunsPerUser: z.number().min(0).max(1000),
})

type AgentLimitsFormValues = z.infer<typeof agentLimitsSchema>

type AgentLimitsSectionProps = {
  defaultValues: AgentLimitsFormValues
}

/**
 * AgentLimitsSection configures the online-experience agent's resource gate
 * (per-user concurrency). LLM calls use the current dashboard user's
 * authenticated playground session, so no shared operator token is required.
 */
export function AgentLimitsSection({ defaultValues }: AgentLimitsSectionProps) {
  const { t } = useTranslation()
  const updateOption = useUpdateOption()

  const form = useForm<AgentLimitsFormValues>({
    resolver: zodResolver(agentLimitsSchema),
    mode: 'onChange',
    defaultValues,
  })

  useEffect(() => {
    form.reset(defaultValues)
  }, [defaultValues, form])

  const onSubmit = async (values: AgentLimitsFormValues) => {
    const updates = Object.entries(values).filter(
      ([key, value]) =>
        value !== defaultValues[key as keyof AgentLimitsFormValues]
    )
    for (const [key, value] of updates) {
      await updateOption.mutateAsync({ key, value: String(value ?? '') })
    }
  }

  return (
    <SettingsSection
      title={t('Online Experience Agent')}
      description={t(
        'Limit how many Agent runs one user may execute at the same time.'
      )}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
          <FormField
            control={form.control}
            name='AgentMaxConcurrentRunsPerUser'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('Max concurrent runs per user')}</FormLabel>
                <FormControl>
                  <Input
                    type='number'
                    min={0}
                    step={1}
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 0)
                    }
                  />
                </FormControl>
                <FormDescription>
                  {t('0 = unlimited. Runs beyond the limit are rejected.')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type='submit' disabled={updateOption.isPending}>
            {updateOption.isPending ? t('Saving...') : t('Save Changes')}
          </Button>
        </form>
      </Form>
    </SettingsSection>
  )
}
